import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { Check, ChevronRight, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachPersona } from '@/components/ask-coach'
import { useGoals } from '@/hooks/use-goals'
import { localDayNumber, useHumanTouch } from '@/lib/human-touch'

type Key = 'firstStep' | 'coach' | 'reminders' | 'habit'
const ORDER: Key[] = ['firstStep', 'coach', 'reminders', 'habit']

// New users' "Getting started" checklist on Today. Steps tick themselves off from
// what the user actually does; it never counts toward stats or streaks.
export function GettingStartedCard({ className }: { className?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const state = useQuery(api.checklist.get)
  const dismiss = useMutation(api.checklist.dismiss).withOptimisticUpdate((store) =>
    store.setQuery(api.checklist.get, {}, null),
  )
  const claimRooted = useMutation(api.checklist.claimRooted)
  const claimed = useRef(false)
  const ensureInbox = useMutation(api.nodes.ensureInbox)
  const goals = useGoals()
  const { name: coach } = useCoachPersona()
  const touch = useHumanTouch()
  const done = state ? ORDER.filter((k) => state[k]).length : 0
  const allDone = done === ORDER.length
  // Finishing the checklist earns the "Rooted" badge (the badge toast celebrates).
  useEffect(() => {
    if (!allDone || claimed.current) return
    claimed.current = true
    void claimRooted({})
  }, [allDone, claimRooted])
  if (!state) return null

  const actions: Record<Key, (() => void) | undefined> = {
    firstStep: undefined,
    coach: () => window.dispatchEvent(new CustomEvent('open-coach')),
    reminders: () => void navigate({ to: '/settings', hash: 'notifications' }),
    habit: async () => {
      if (!goals?.some((g) => g.isInbox)) await ensureInbox()
      void navigate({ to: '/everyday', search: { addTask: true, tab: 'habits' } })
    },
  }

  return (
    <section
      className={cn(
        'rounded-2xl border border-primary/25 bg-primary/[0.06] px-3.5 py-3',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <p className="text-[13px] font-extrabold">
          {allDone ? t('checklist.allDone') : t('checklist.title')}
        </p>
        <span className="font-mono text-[11px] text-muted-foreground">
          {t('checklist.progress', { done, total: ORDER.length })}
        </span>
        <button
          type="button"
          onClick={() => void dismiss({})}
          aria-label={t('common.close')}
          className="ml-auto flex size-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-primary/15">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${(done / ORDER.length) * 100}%` }}
        />
      </div>
      {allDone ? (
        <p className="mt-2 text-xs text-muted-foreground">{t('checklist.allDoneDesc')}</p>
      ) : (
        <ul className="mt-2 flex flex-col">
          {ORDER.map((k) => {
            const ok = state[k]
            const run = ok ? undefined : actions[k]
            return (
              <li key={k}>
                <button
                  type="button"
                  disabled={!run}
                  onClick={() => run?.()}
                  className="flex w-full items-center gap-2.5 rounded-lg px-1 py-1.5 text-left text-[13px] enabled:cursor-pointer enabled:hover:bg-primary/[0.06]"
                >
                  <span
                    className={cn(
                      'flex size-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
                      ok ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                    )}
                  >
                    {ok && <Check className="size-3" strokeWidth={3} />}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1',
                      ok ? 'text-muted-foreground line-through' : 'font-semibold',
                    )}
                  >
                    {t(`checklist.${k}`, { coach })}
                  </span>
                  {run && <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
      <p className="mt-2 text-[11px] italic text-muted-foreground/60">
        {touch.line(localDayNumber() - localDayNumber(state.since ?? Date.now()))}
      </p>
    </section>
  )
}
